/**
 * Ribonia - Customily add-on quantity sync
 *
 * Keeps the "Personalizare panglica" add-on lines (print serigrafic,
 * print 3D, folie metalizata) at the same quantity as the ribbon lines
 * that use them. Runs on page load, after every cart change made by the
 * theme or by Customily, and right before checkout.
 *
 * Install: upload as assets/customily-addon-sync.js and add to
 * layout/theme.liquid, just before </body>:
 *   <script src="{{ 'customily-addon-sync.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  const CONFIG = {
    // The add-on product (Shopify handle + its variant IDs)
    addonHandle: 'personalizare-panglica',
    addonVariantIds: [60750686814542, 60750686847310, 60750686880078],
    // Line item property on the ribbon that holds the chosen method,
    // e.g. "Print serigrafic într-o culoare (+37,00 lei)"
    methodPropertyName: 'Metodă personalizare',
    debounceMs: 400,
    // An add-on without its ribbon is removed only after this long
    orphanGraceMs: 5000,
    debug: true // set to false once everything works
  };

  let orphanSeen = new Map();

  const nativeFetch = window.fetch.bind(window);
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  let timer = null;
  let running = null;

  const log = (...a) => CONFIG.debug && console.log('[addon-sync]', ...a);
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

  function isAddon(item) {
    return item.handle === CONFIG.addonHandle ||
      CONFIG.addonVariantIds.includes(Number(item.variant_id));
  }

  function methodOf(item) {
    const props = item.properties || {};
    let val = props[CONFIG.methodPropertyName];
    if (val == null) {
      // fallback: any visible property whose name contains "metod"
      const key = Object.keys(props).find((k) => k[0] !== '_' && /metod/i.test(k));
      if (key) val = props[key];
    }
    return val == null ? null : norm(val);
  }

  /**
   * Primary method: Customily links lines with properties.
   *   ribbon:  _customily-upsell-main-id = X
   *   add-on:  _customily-upsell-id      = X  (+ _customily-upsell-multiplier)
   * Add-on qty = ribbon qty x multiplier. Add-on with no ribbon = removed.
   *
   * Fallback (add-ons without the link property): required qty = sum of
   * ribbon quantities whose method property starts with the add-on title.
   *
   * Returns { lineKey: newQty } or null when nothing needs changing.
   */
  function computeUpdates(cart) {
    const addons = cart.items.filter(isAddon);
    if (!addons.length) return null;

    const parents = cart.items.filter((i) => !isAddon(i));
    const updates = {};
    const nextOrphans = new Map();
    log('cart', cart.items.map((i) => ({ title: i.product_title, variant: i.variant_title, qty: i.quantity, props: i.properties })));

    // 1) Linked add-ons
    const parentById = {};
    parents.forEach((p) => {
      const id = (p.properties || {})['_customily-upsell-main-id'];
      if (id) parentById[id] = p;
    });

    const unlinked = [];
    addons.forEach((a) => {
      const props = a.properties || {};
      const id = props['_customily-upsell-id'];
      if (!id) { unlinked.push(a); return; }
      const parent = parentById[id];
      if (!parent) {
        // Customily may add the add-on before the ribbon. Only remove an
        // orphan if it was already orphaned on the previous check.
        const firstSeen = orphanSeen.get(a.key) || Date.now();
        nextOrphans.set(a.key, firstSeen);
        if (Date.now() - firstSeen > CONFIG.orphanGraceMs) {
          log('removing orphan add-on', a.variant_title);
          updates[a.key] = 0;
        } else {
          log('orphan add-on, waiting for its ribbon', a.variant_title);
        }
        return;
      }
      const multiplier = Math.max(1, parseInt(props['_customily-upsell-multiplier'], 10) || 1);
      const target = parent.quantity * multiplier;
      if (a.quantity !== target) updates[a.key] = target;
    });
    orphanSeen = nextOrphans;

    // 2) Fallback for add-ons without a link
    const parentsWithMethod = parents.filter((p) => methodOf(p));
    const byVariant = {};
    unlinked.forEach((a) => (byVariant[a.variant_id] ||= []).push(a));

    Object.values(byVariant).forEach((lines) => {
      const title = norm(lines[0].variant_title);
      const required = parentsWithMethod.reduce(
        (sum, p) => (title && methodOf(p).startsWith(title) ? sum + p.quantity : sum),
        0
      );

      // Safety: if ribbons with a method exist but none match this add-on,
      // something is misconfigured. Leave the add-on alone instead of
      // removing a paid line.
      // Never remove unlinked add-ons here: if nothing matches, leave them.
      if (required === 0) {
        if (parentsWithMethod.length) console.warn('[addon-sync] No ribbon line matches add-on "' + lines[0].variant_title + '". Check CONFIG.methodPropertyName.');
        return;
      }

      // First line carries the full quantity, any duplicates go to 0.
      lines.forEach((line, idx) => {
        const target = idx === 0 ? required : 0;
        if (line.quantity !== target) updates[line.key] = target;
      });
    });

    return Object.keys(updates).length ? updates : null;
  }

  async function getCart() {
    const r = await nativeFetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    return r.json();
  }

  async function sync() {
    if (running) return running;
    running = (async () => {
      try {
        const cart = await getCart();
        fixCounts(cart);
        const updates = computeUpdates(cart);
        if (!updates) return false;
        log('updating', updates);
        const r = await nativeFetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        const newCart = await r.json();
        fixCounts(newCart);
        await refreshUI(newCart);
        return true;
      } catch (e) {
        console.error('[addon-sync]', e);
        return false;
      } finally {
        running = null;
      }
    })();
    return running;
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, CONFIG.debounceMs);
  }

  /**
   * Cart count without the add-ons (header bubble + drawer title).
   * The theme sets these from cart.item_count after each change, so this
   * runs after every sync and corrects them.
   */
  function fixCounts(cart) {
    const n = cart.items
      .filter((i) => !(i.properties || {})['_customily-upsell-id'])
      .reduce((sum, i) => sum + i.quantity, 0);
    document.querySelectorAll('[data-cart-count-number]').forEach((el) => {
      if (el.textContent.trim() !== String(n)) el.textContent = n;
    });
  }

  /**
   * Re-render the cart drawer from the server (Section Rendering API),
   * so line totals and add-on lines match the synced cart.
   */
  async function refreshDrawer() {
    const inner = document.querySelector('#cart-drawer [data-drawer-inner="cart"]');
    if (!inner) return;
    try {
      const r = await nativeFetch(location.pathname + '?section_id=cart-drawer', { credentials: 'same-origin' });
      if (!r.ok) { log('drawer refresh failed', r.status); return; }
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const fresh = doc.querySelector('[data-drawer-inner="cart"]');
      if (!fresh) { log('drawer markup not found in section response'); return; }
      inner.innerHTML = fresh.innerHTML;
      const freshDrawer = fresh.closest('cart-drawer');
      const drawer = inner.closest('cart-drawer');
      if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    } catch (e) {
      console.error('[addon-sync] drawer refresh', e);
    }
  }

  /**
   * Re-render after a sync. On the cart page a reload is the safest way
   * to get correct totals; elsewhere the drawer is re-rendered in place.
   */
  async function refreshUI(cart) {
    document.dispatchEvent(new CustomEvent('ribonia:addons-synced', { detail: { cart } }));
    if (/\/cart\/?$/.test(location.pathname)) {
      location.reload();
      return;
    }
    await refreshDrawer();
    fixCounts(cart);
  }

  // Watch cart changes made via fetch (theme, Customily)
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = nativeFetch(input, init);
    if (CART_MUTATION.test(url)) p.then(schedule, () => {});
    return p;
  };

  // ...and via XMLHttpRequest (older scripts / jQuery)
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(String(url))) this.addEventListener('load', schedule);
    return nativeOpen.apply(this, arguments);
  };

  // Last check before checkout, in case a change slipped through
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[name="checkout"], a[href^="/checkout"], button[form][name="checkout"]');
    if (!btn || btn.dataset.addonSynced) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    await sync();
    btn.dataset.addonSynced = '1';
    window.location.href = '/checkout';
  }, true);

  // Fix carts that are already out of sync
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();